import React, { FormEvent, useState } from 'react';
import Button from '@mui/material/Button';
import { Box, Typography } from '@mui/material';
import { StyleSheet } from 'react-native';
import { useThemeContext } from '../../hook/useThemeManager';
import { ColorScheme, theme } from '../../constants/theme';
import { useMultistepForm } from './useMultistepForm';
import { useEsHover } from '../Animation/useEsHover';
import { Signup } from './Signup';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { EsVerifyCode } from '../EsVerify/EsVerifyCode';
import { TUserField } from '../../redux/user/actions';
import { ScreenTypes } from '../../module/ThemeManager';
import { strings } from '../../constants';
import { Spinner } from '@chakra-ui/react';
import { OTPPage } from './OTPPage';
import { Interests, TOnChangePayload } from './Interests';
import { validate } from '../../utils/validators';
import { isEmpty } from 'lodash';
import { CountryData } from 'react-phone-input-2';

export type SignUpdata = {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    userName?: string;
    birth?: string;
    gender?: string;
    country: string;
    phoneNumber: string;
    verificationChannel?: 'sms' | 'whatsapp' | 'audme-app' | 'email';
    fullName: string;
};

const SIGNUP_DATA: SignUpdata = {
    email: '',
    password: '',
    country: '',
    phoneNumber: '',
    fullName: '',
    firstName: '',
    lastName: '',
    verificationChannel: 'sms',
};

interface ISignUpProps {
    onSubmitSignUpOTP: (data: SignUpdata, code?: string[], callBack?: () => void) => void;
    checkUserDataAvailability: (data: SignUpdata, callBack: () => void, field: TUserField, step: number) => void;
    userNameAvailable: (userName: string) => void;
    getSignUpOTP: (data: SignUpdata, callBack: () => void, resend?: boolean) => void | Promise<unknown>;
    updateUserDetails: (details: TOnChangePayload, callBack?: () => void) => void;
    loading?: boolean;
    otpLoading?: boolean;
    resendInProgress?: boolean;
    formStyle?: React.CSSProperties;
}

const EsSignupForm: React.FC<ISignUpProps> = ({
    onSubmitSignUpOTP,
    checkUserDataAvailability,
    updateUserDetails,
    getSignUpOTP,
    loading,
    otpLoading,
    resendInProgress,
    formStyle,
}) => {
    const [data, setData] = useState(SIGNUP_DATA);
    const [details, setDetails] = useState<TOnChangePayload>({ interest: [], user_type: [] });
    const [otp, setOtp] = useState<string[]>();
    const [messagePreference, setMessagePreference] = useState<
        'sms' | 'whatsapp' | 'audme-app' | 'email' | undefined
    >();
    const [countryCode, setCountryCode] = useState<CountryData>();

    function updateFields(fields: Partial<SignUpdata>) {
        setData(prev => {
            return { ...prev, ...fields };
        });
    }

    const { themeStyle, device } = useThemeContext();

    const { handleMouseEnter, handleMouseLeave, isHover } = useEsHover();

    const handlePhoneNumberCountryChange = (d?: CountryData, isFocused?: boolean) => {
        setCountryCode(d);
    };

    const { steps, currentStepIndex, step, isFirstStep, isLastStep, back, next } = useMultistepForm([
        <Signup {...data} updateFields={updateFields} onCountryChange={handlePhoneNumberCountryChange} />,

        <OTPPage
            title="Select OTP Preference"
            subtitle="Select where you would like to recieve your OTP"
            phoneNumber={`+${data.phoneNumber}`}
            email={data.email}
            onSetOTPPreference={val => {
                setMessagePreference(val);
                setData(prevData => ({
                    ...prevData,
                    verificationChannel: val,
                }));
            }}
        />,

        <EsVerifyCode
            {...data}
            onResendOtp={() => getSignUpOTP(data, () => {}, true)}
            onClickBack={() => back()}
            onChange={(otp: string[]) => setOtp(otp)}
            loading={otpLoading}
            resendInProgress={resendInProgress}
            buttonString="Continue"
            style={{
                padding: 20,
            }}
        />,
        <Interests onChange={setDetails} />,
    ]);

    const styles = dynamicStyles(themeStyle, isHover, device);
    function onSubmit(e: FormEvent) {
        e.preventDefault();
        switch (currentStepIndex) {
            case 0:
                checkUserDataAvailability(data, next, 'email', currentStepIndex);
                break;
            case 1:
                getSignUpOTP(data, next);
                break;
            case 2:
                onSubmitSignUpOTP(data, otp, next);
                break;
            case 3:
                /** Go Home */
                updateUserDetails(details, next);
                break;
        }
    }
    const _validatePasswordCharacters = (pass: any) => {
        const passwordLength = validate.validatePasswordLength(pass);
        const passwordhasNumber = validate.validatePasswordNumber(pass);
        const passwordHascase = validate.validatePasswordCase(pass);
        return passwordLength && passwordhasNumber && passwordHascase;
    };
    const enableButton = () => {
        if (currentStepIndex === 0) {
            const isValid =
                !isEmpty(data.email) &&
                validate.validateEmail(data.email) &&
                !isEmpty(data.password) &&
                _validatePasswordCharacters(data.password) &&
                !isEmpty(data.phoneNumber) &&
                validate.validatePhoneNumber(data.phoneNumber, countryCode?.countryCode);
            return isValid;
        }

        if (currentStepIndex === 1) {
            return !isEmpty(messagePreference);
        }

        if (currentStepIndex === 2) {
            return true;
        }

        if (currentStepIndex === 3) {
            return details.interest.length > 0 && details.user_type.length > 0;
        }
    };

    const verificationSelection = messagePreference === 'sms' ? 'SMS' : 'WhatsApp™';

    return (
        <Box>
            <form
                style={{
                    ...styles.form,
                    ...formStyle,
                    ...(isLastStep && { minWidth: device.isDesktop ? '600px' : '300px' }),
                }}
                onSubmit={onSubmit}>
                <Box>
                    <Typography style={styles.steps}>
                        STEP {currentStepIndex + 1} OF {steps.length}
                    </Typography>
                </Box>
                <Box>{step}</Box>

                {isFirstStep && (
                    <Button
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        style={!enableButton() ? styles.disabledButton : styles.button}
                        disabled={!enableButton() || loading}
                        type="submit">
                        {loading ? (
                            <Spinner h={4} w={4} color={theme[themeStyle].tag_white} />
                        ) : (
                            <Typography style={!enableButton() ? styles.signupDisabled : styles.signup}>
                                {strings.sign_up}
                            </Typography>
                        )}
                    </Button>
                )}

                {isFirstStep && (
                    <Typography style={styles.text}>
                        By clicking Sign Up, you agree to our Terms, Privacy Policy and Cookies Policy. You may receive
                        SMS and/or WhatsApp™ notifications [and or emails] from us and can opt out at any time. Thspian
                        will use {verificationSelection} for verification based on your selection.
                    </Typography>
                )}
                {!isFirstStep && !isLastStep && currentStepIndex !== 2 && (
                    <Button
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        style={!enableButton() ? styles.disabledButton : styles.button}
                        disabled={!enableButton()}
                        type="submit">
                        <Typography style={!enableButton() ? styles.signupDisabled : styles.signup}>
                            {isLastStep ? 'Finish' : 'Next'}
                        </Typography>
                    </Button>
                )}
                {!isFirstStep && !isLastStep && currentStepIndex !== 2 && (
                    <Button style={styles.backButton} onClick={back} type="button">
                        <Typography style={styles.backText}>{strings.go_back}</Typography>
                    </Button>
                )}
                {isLastStep && (
                    <Button
                        onMouseEnter={handleMouseEnter}
                        onMouseLeave={handleMouseLeave}
                        style={!enableButton() ? styles.disabledButton : styles.button}
                        disabled={!enableButton()}
                        type="submit">
                        <Typography style={!enableButton() ? styles.signupDisabled : styles.signup}>
                            {'Done'}
                        </Typography>
                    </Button>
                )}
            </form>
        </Box>
    );
};

const dynamicStyles = (colorScheme: ColorScheme, isHover: boolean, device: ScreenTypes) =>
    StyleSheet.create({
        form: {
            width: '100%',
            padding: device.isMobile ? 30 : 50,
            maxWidth: 500,
            height: 'auto',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            backgroundColor: theme[colorScheme].form_black,
            borderRadius: 20,
            marginTop: 15,
        },
        steps: {
            fontSize: 11,
            letterSpacing: 1.98,
            fontWeight: '500',
            color: theme[colorScheme].primary_orange,
            fontFamily: FONT_ENUM.MAIN_REGULAR,
        },
        signup: {
            fontSize: 14,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_BOLD,
            color: isHover ? theme[colorScheme].gray_text : theme[colorScheme].true_black,
            height: 20,
        },
        signupDisabled: {
            fontSize: 14,
            fontWeight: '600',
            fontFamily: FONT_ENUM.MAIN_BOLD,
            color: theme[colorScheme].true_highlight,
            height: 20,
        },
        button: {
            width: '100%',
            height: 44,
            paddingHorizontal: 20,
            paddingVertical: 12,
            borderRadius: 100,
            maxWidth: 400,
            backgroundColor: theme[colorScheme].true_highlight,
            textTransform: 'none',
            alignSelf: 'center',
            marginTop: 10,
        },
        disabledButton: {
            width: '100%',
            height: 44,
            paddingHorizontal: 20,
            paddingVertical: 12,
            borderRadius: 100,
            maxWidth: 400,
            backgroundColor: theme[colorScheme].tag_white_light,
            borderColor: theme[colorScheme].highlight_to_white,
            textTransform: 'none',
            alignSelf: 'center',
            marginTop: 10,
        },
        text: {
            maxWidth: 400,
            width: '100%',
            fontSize: 12,
            fontStyle: 'normal',
            fontWeight: '400',
            color: theme[colorScheme].light_gray_text,
            marginTop: 20,
            fontFamily: FONT_ENUM.MAIN_REGULAR,
        },
        backText: {
            textAlign: 'center',
            fontSize: 12,
            fontStyle: 'normal',
            fontWeight: '400',
            color: theme[colorScheme].lightWhite,
            fontFamily: FONT_ENUM.MAIN_REGULAR,
        },
        backButton: {
            width: 'fit-content',
            alignSelf: 'center',
            height: 'fit-content',
            paddingHorizontal: 20,
            paddingVertical: 5,
            backgroundColor: theme[colorScheme].transparent,
            textTransform: 'none',
            marginTop: 20,
        },
    });

export { EsSignupForm };
